import { SelectMenu, Typography } from '@scale/scaleui';
import {
  RubricDimension,
  RubricRating,
  RubricDimensionRating,
} from '@scale/shared/types/params/textcollection';

interface RubricDimensionsDropdownsProps {
  ratingsWithoutDimensions: RubricRating[];
  ratingWithoutDimensionForCriteria: string | undefined;
  allDimensions: RubricDimension[];
  ratingsForCriteria: { [key: string]: RubricDimensionRating };
  updateRating: ({ rating, dimension }: { rating: string; dimension?: string }) => void;
  disabled?: boolean;
}

/**
 * This component uses SelectMenu from scaleui instead of Select from scaleui-radix
 * due to performance issues with the latter.
 *
 * https://linear.app/scale-epd/issue/CHAT-1687/rubrics-section-really-slow
 */
export const RubricDimensionsDropdowns = ({
  ratingsWithoutDimensions,
  ratingWithoutDimensionForCriteria,
  allDimensions,
  ratingsForCriteria,
  updateRating,
  disabled,
}: RubricDimensionsDropdownsProps) => {
  if (allDimensions.length === 0) {
    return (
      <SelectMenu
        options={ratingsWithoutDimensions.map(rating => ({
          id: rating.value,
          name: rating.label,
        }))}
        renderValue={selectedOptionId =>
          renderRating(ratingsWithoutDimensions.find(r => r.value === selectedOptionId))
        }
        selectedOptionId={ratingWithoutDimensionForCriteria ?? ''}
        setSelected={value => {
          updateRating({ rating: value });
        }}
        disabled={disabled}
      />
    );
  }

  return (
    <div className="grid grid-cols-2 gap-3">
      {allDimensions.map(dimension => {
        const ratingForDimension = ratingsForCriteria[dimension.value];
        return (
          <div className="flex flex-col gap-2" key={dimension.value}>
            <Typography.Paragraph3>{dimension.label}</Typography.Paragraph3>
            <SelectMenu
              options={dimension.ratings.map(rating => ({
                id: rating.value,
                name: rating.label,
              }))}
              renderValue={selectedOptionId =>
                renderRating(dimension.ratings.find(r => r.value === selectedOptionId))
              }
              selectedOptionId={ratingForDimension?.rating ?? ''}
              setSelected={value => {
                updateRating({ rating: value, dimension: dimension.value });
              }}
              disabled={disabled}
            />
          </div>
        );
      })}
    </div>
  );
};

function renderRating(rating: RubricRating | undefined) {
  if (!rating) {
    return null;
  }
  return (
    <div className="flex items-center">
      {rating.color && (
        <div className={`mr-2 inline-block h-3 w-3 rounded-sm bg-${rating.color}-100`} />
      )}
      {rating.label}
    </div>
  );
}
